import {
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

function getNotchPositions(
  height,
  notchSize,
  notchGap,
  notchOffset
) {
  const period = notchSize + notchGap;
  const positions = [];

  // Use a manually provided starting position.
  if (typeof notchOffset === "number") {
    for (
      let y = notchOffset;
      y + notchSize <= height;
      y += period
    ) {
      positions.push(y);
    }

    return positions;
  }

  // Otherwise center the pattern vertically.
  const count = Math.max(
    1,
    Math.floor((height + notchGap) / period)
  );

  const patternHeight =
    count * notchSize + (count - 1) * notchGap;

  const start = Math.max(0, (height - patternHeight) / 2);

  for (let index = 0; index < count; index += 1) {
    positions.push(start + index * period);
  }

  return positions;
}

function createTicketPath({
  width,
  height,
  notchSize,
  notchGap,
  notchOffset,
}) {
  if (!width || !height) {
    return "";
  }

  const radius = notchSize / 2;

  const positions = getNotchPositions(
    height,
    notchSize,
    notchGap,
    notchOffset
  );

  const path = [
    `M 0 0`,
    `H ${width}`,
  ];

  // Right edge: travel downward.
  positions.forEach((top) => {
    const bottom = top + notchSize;

    path.push(`V ${top}`);

    // Inward-facing semicircle.
    path.push(
      `A ${radius} ${radius} 0 0 0 ${width} ${bottom}`
    );
  });

  path.push(`V ${height}`);
  path.push(`H 0`);

  // Left edge: travel upward.
  [...positions].reverse().forEach((top) => {
    const bottom = top + notchSize;

    path.push(`V ${bottom}`);

    // Inward-facing semicircle.
    path.push(
      `A ${radius} ${radius} 0 0 0 0 ${top}`
    );
  });

  path.push("V 0");
  path.push("Z");

  return path.join(" ");
}

export default function TicketCutout({
  children,
  className = "",
  contentClassName = "",
  style = {},

  // Cutout settings
  notchSize = 8,
  notchGap = 8,

  // Leave undefined to automatically center the pattern.
  notchOffset,

  // Ticket appearance
  backgroundColor = "#ffffff",

  // Shadow appearance
  shadowColor = "#0f172a",
  shadowOpacity = 0.4,
  shadowOffsetX = 0,
  shadowOffsetY = 0,
  shadowBlur = 1,
}) {
  const containerRef = useRef(null);

  const [size, setSize] = useState({
    width: 0,
    height: 0,
  });

  const reactId = useId();
  const filterId = `ticket-shadow-${reactId.replace(/:/g, "")}`;

  useLayoutEffect(() => {
    const element = containerRef.current;

    if (!element) {
      return undefined;
    }

    const updateSize = () => {
      const bounds = element.getBoundingClientRect();

      setSize({
        width: bounds.width,
        height: bounds.height,
      });
    };

    updateSize();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(updateSize);

      observer.observe(element);

      return () => observer.disconnect();
    }

    window.addEventListener("resize", updateSize);

    return () => {
      window.removeEventListener("resize", updateSize);
    };
  }, []);

  const path = createTicketPath({
    width: size.width,
    height: size.height,
    notchSize,
    notchGap,
    notchOffset,
  });

  return (
    <div
      ref={containerRef}
      className={`relative isolate overflow-visible ${className}`}
      style={style}
    >
      {size.width > 0 && size.height > 0 && (
        <svg
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 h-full w-full overflow-visible"
          viewBox={`0 0 ${size.width} ${size.height}`}
          preserveAspectRatio="none"
          style={{
            overflow: "visible",
          }}
        >
          <defs>
            <filter
              id={filterId}
              x="-50%"
              y="-50%"
              width="200%"
              height="200%"
              colorInterpolationFilters="sRGB"
            >
              <feDropShadow
                dx={shadowOffsetX}
                dy={shadowOffsetY}
                stdDeviation={shadowBlur}
                floodColor={shadowColor}
                floodOpacity={shadowOpacity}
              />
            </filter>
          </defs>

          <path
            d={path}
            fill={backgroundColor}
            filter={`url(#${filterId})`}
          />
        </svg>
      )}

      {/* Content is separate, so it cannot be clipped. */}
      <div className={`relative z-10 font-[Lekton] ${contentClassName}`}>
        {children}
      </div>
    </div>
  );
}